import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft, FaEye, FaEyeSlash } from "react-icons/fa";
import { Button } from "../../NextComponents/Button";
import { TextField } from "../../NextComponents/Fields";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import { Link } from "react-router-dom";
import { baseUrl } from "../../../Environments/environment";
import onelyk from "../../../Assets/onelyklogo.svg";
import { LocalStorage } from "../../../utils";
import { apiClient, postGoogleAuth } from "../../../api";
import UserValidationSchema from "../../../utils/userValidation";
import { z } from "zod";
import AuthOrDivider from "../AuthOrDivider";
import GoogleAuthButton from "../GoogleAuthButton";
import ConditionsModal from "../../Conditions/ConditionsModal";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import ReCaptchaField from "../../Common/ReCaptchaField";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../../Contexts/AuthContext/AuthContext";
import { useGoogleLogin, useGoogleOAuth } from "@react-oauth/google";

import { useSearchParams } from "react-router-dom";
export default function Register() {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
	});
	const [otp, setOtp] = useState("");
	const [isRegistering, setIsRegistering] = useState(true);
	const [verified, setVerified] = useState(false);
	const router = useNavigate();
	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [resendCountdown, setResendCountdown] = useState(0);
	const [inputErrors, setInputErrors] = useState({});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalContent, setModalContent] = useState("");
	const [isChecked, setIsChecked] = useState(false);
	const [captchaToken, setCaptchaToken] = useState(null);
	const { syncAuthFromStorage } = useAuth();
	const [googleLoading, setGoogleLoading] = useState(false);
	const [signupStep, setSignupStep] = useState("form");
	const [googlePasswordForm, setGooglePasswordForm] = useState({
		password: "",
		confirmPassword: "",
	});
	const [googleSignupProfile, setGoogleSignupProfile] = useState(null);
	const { scriptLoadedSuccessfully } = useGoogleOAuth();

	const resetGoogleSignup = () => {
		setGoogleSignupProfile(null);
		setGooglePasswordForm({ password: "", confirmPassword: "" });
		setSignupStep("form");
		setIsRegistering(true);
		setInputErrors({});
	};

	const GooglePasswordSchema = z
		.object({
			password: UserValidationSchema.shape.password,
			confirmPassword: z.string().min(1, "Please confirm your password"),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: "Passwords do not match",
			path: ["confirmPassword"],
		});

	const persistGoogleSession = (result) => {
		LocalStorage.set("accessToken", result.accessToken);
		LocalStorage.set("user", JSON.stringify(result.user));
		syncAuthFromStorage();
	};

	const handleGoogleSignupCheck = async (code) => {
		setGoogleLoading(true);
		setIsLoading(true);
		setInputErrors({});
		try {
			const response = await postGoogleAuth({ code, intent: "check" });
			const { result } = response.data ?? {};

			if (result?.needsPasswordSetup) {
				const profile = {
					name: result.name ?? result.user?.name ?? "",
					email: result.email ?? result.user?.email ?? "",
					googleId: result.googleId ?? result.user?.googleId ?? "",
				};

				if (!profile.googleId || !profile.email) {
					toast.error("Invalid response from Google sign-in");
					return;
				}

				setGoogleSignupProfile(profile);
				setSignupStep("google-password");
				setIsRegistering(false);
				setInputErrors({});
				return;
			}

			if (!result?.accessToken) {
				toast.error("Invalid response from Google sign-in");
				return;
			}

			persistGoogleSession(result);
			toast.success("Welcome back! You're signed in.");
			router("/propertyhome", { replace: true });
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				"Google sign-up failed. Please try again.";
			toast.error(message);
		} finally {
			setGoogleLoading(false);
			setIsLoading(false);
		}
	};

	const startGooglePopup = useGoogleLogin({
		flow: "auth-code",
		ux_mode: "popup",
		onSuccess: (codeResponse) => {
			handleGoogleSignupCheck(codeResponse.code);
		},
		onError: () => {
			toast.error("Google sign-in was not authorized.");
			setGoogleLoading(false);
		},
		onNonOAuthError: () => {
			setGoogleLoading(false);
		},
	});

	const handleGooglePasswordChange = (e) => {
		const { name, value } = e.target;
		setGooglePasswordForm((prev) => ({ ...prev, [name]: value }));
	};

	const handleGooglePasswordSubmit = async (e) => {
		e.preventDefault();

		if (!googleSignupProfile?.googleId || !googleSignupProfile?.email) {
			toast.error("Google sign-up session expired. Please sign up with Google again.");
			resetGoogleSignup();
			return;
		}

		if (!isChecked) {
			setInputErrors({
				termsAccepted: "You must accept the terms and privacy policy.",
			});
			return;
		}

		const validation = GooglePasswordSchema.safeParse(googlePasswordForm);
		if (!validation.success) {
			const errors = {};
			validation.error.errors.forEach((err) => {
				errors[err.path[0]] = err.message;
			});
			setInputErrors(errors);
			return;
		}

		setIsLoading(true);
		setInputErrors({});
		try {
			const response = await postGoogleAuth({
				name: googleSignupProfile.name,
				email: googleSignupProfile.email,
				googleId: googleSignupProfile.googleId,
				password: googlePasswordForm.password,
				confirmPassword: googlePasswordForm.confirmPassword,
				intent: "signup",
			});
			const { result } = response.data ?? {};

			if (!result?.accessToken) {
				toast.error("Invalid response from Google sign-in");
				return;
			}

			persistGoogleSession(result);
			setGoogleSignupProfile(null);
			setGooglePasswordForm({ password: "", confirmPassword: "" });
			toast.success("Account created successfully!");
			router("/propertyhome", { replace: true });
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				"Google sign-up failed. Please try again.";
			toast.error(message);
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleClick = () => {
		if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
			toast.info(
				"Add REACT_APP_GOOGLE_CLIENT_ID to enable Google popup sign-in. Your backend should accept POST /api/auth/google.",
			);
			return;
		}
		if (!scriptLoadedSuccessfully) {
			toast.warning(
				"Google sign-in is still loading. Try again in a moment.",
			);
			return;
		}
		setGoogleLoading(true);
		startGooglePopup();
	};

	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	useEffect(() => {
		if (token) {
			apiClient.get(`${baseUrl}/userInvite/verify?token=${token}`)
				.catch((error) => {
					console.error("Invalid token error:", error);
					toast.error("Invalid or expired invite token");
					router("/");
				});
		}
	}, [token, router]);

	useEffect(() => {
		let timer;
		if (resendCountdown > 0) {
			timer = setTimeout(() => {
				setResendCountdown(resendCountdown - 1);
			}, 1000);
		}
		return () => clearTimeout(timer);
	}, [resendCountdown]);
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const handleOtpChange = (e) => {
		const { value } = e.target;
		setOtp(value);
	};

	const handleRegisterSubmit = async (e) => {
		e.preventDefault();
		if (!captchaToken) {
			setInputErrors({ captcha: "Please verify captcha" });
			return;
		}
		setIsLoading(true);

		// Validate form data using Zod schema
		const validationResult = UserValidationSchema.safeParse(formData);

		const dataToSend = {
			name: formData.name,
			email: formData.email,
			password: formData.password,
			captchaToken: captchaToken,
		};

		// Initialize an object to store error messages
		let errors = {};

		// If validation fails, store the errors
		if (!validationResult.success) {
			validationResult.error.errors.forEach((err) => {
				errors[err.path[0]] = err.message; // Set the error message for the specific field
			});
			if (!isChecked) {
				errors["termsAccepted"] =
					"You must accept the terms and privacy policy.";
			}

			// Update the inputErrors state with the validation errors
			setInputErrors(errors);

			setIsLoading(false); // Stop loading
			return; // Prevent request from being sent
		}
		const requestHandler2 = async (api, setLoading, onSuccess, onError) => {
			// Show loading state if setLoading function is provided
			if (setLoading) setLoading(true);
			try {
				// Make the API request
				const response = await api();
				const { data } = response;
				if (data?.statusCode < 400) {
					// Call the onSuccess callback with the response data
					onSuccess(data);
				}
			} catch (error) {
				// Handle error cases, including unauthorized and forbidden cases
				if ([403].includes(error?.response?.data?.statusCode)) {
					localStorage.clear(); // Clear local storage on authentication issues
					window.location.href = "/"; // Redirect to login page
				}
				// for Input Type validation errors
				if (409 === error?.response?.data?.statusCode) {
					const keys = Object.keys(error?.response?.data?.error);
					const errorArray = error?.response?.data?.error[keys[0]];
					for (let i = 0; i < keys.length; i++) {
						setInputErrors({ [keys[i]]: errorArray[i] });
					}

					onError(
						errorArray[0] ||
							error?.response?.data?.message ||
							"Something went wrong",
					);
				} else {
					onError(error?.response?.data?.message || "Something went wrong");
				}
			} finally {
				// Hide loading state if setLoading function is provided
				if (setLoading) setLoading(false);
			}
		};

		requestHandler2(
			async () => await apiClient.post(`${baseUrl}/user/register`, dataToSend),
			setIsLoading,
			(res) => {
				setIsRegistering(false);
				setSignupStep("otp");
				toast.success("Otp send to your email!");
				setResendCountdown(60);
				setInputErrors({});
			},
			(e) => {
				console.log(e);
				toast.error(e);
			},
		);
	};

	const handleResendOtp = async () => {
		setIsLoading(true);

		try {
			await apiClient.post(`${baseUrl}/auth/resendOtp`, {
				email: formData.email,
			});
			toast.success("OTP resent successfully!");
			setResendCountdown(60);
		} catch (error) {
			const errorMessage =
				error.response?.data?.message ||
				"Failed to resend OTP. Please try again.";
			console.error(errorMessage);
			toast.error(errorMessage);
		} finally {
			setIsLoading(false);
		}
	};

	const handleOtpSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			let response;
			if (token) {
				response = await apiClient.post(`${baseUrl}/auth/verifyOtpInvite`, {
					email: formData.email,
					otp,
					token,
				});
			} else {
				response = await apiClient.post(`${baseUrl}/auth/verifyOtp`, {
					email: formData.email,
					otp,
				});
			}

			const result = response?.data?.result || {};
			const accessToken = result.accessToken || response?.data?.accessToken;
			const userFromResponse = result.user || response?.data?.user;

			if (!accessToken) {
				throw new Error("Access token not found in OTP verification response");
			}

			LocalStorage.set("accessToken", accessToken);

			if (userFromResponse?._id) {
				LocalStorage.set("user", JSON.stringify(userFromResponse));
			} else {
				const decoded = jwtDecode(accessToken);
				LocalStorage.set(
					"user",
					JSON.stringify({
						_id: decoded?._id || decoded?.id || decoded?.userId || decoded?.sub,
						role: decoded?.role,
					}),
				);
			}
			syncAuthFromStorage();

			setVerified(true);
			toast.success("OTP verified. You are now logged in.");
			router("/propertyhome", { replace: true });
		} catch (error) {
			console.error(error);
			toast.error("Wrong Otp!");
			// Handle error response
		} finally {
			setIsLoading(false);
		}
	};

	const openModal = (type) => {
		if (type === "privacy") {
			setModalContent("privacy");
		} else if (type === "terms") {
			setModalContent("terms");
		}
		console.log(type);
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setModalContent("");
	};

	return (
		<div className="relative min-h-screen overflow-hidden bg-gray-800">
			<div aria-hidden="true" className="hidden sm:absolute sm:inset-0 sm:block">
				<svg
					fill="none"
					width={364}
					height={384}
					viewBox="0 0 364 384"
					className="absolute bottom-0 right-0 mb-48 translate-x-1/2 transform text-gray-700 lg:top-0 lg:mb-0 lg:mt-28 xl:translate-x-0 xl:transform-none"
				>
					<defs>
						<pattern
							x={0}
							y={0}
							id="register-pattern"
							width={20}
							height={20}
							patternUnits="userSpaceOnUse"
						>
							<rect x={0} y={0} fill="currentColor" width={4} height={4} />
						</pattern>
					</defs>
					<rect fill="url(#register-pattern)" width={364} height={384} />
				</svg>
			</div>
			<div className="relative z-10 flex min-h-screen flex-col">
			{isLoading && <LoadingSpinner />}
			<div className="mx-auto flex w-full max-w-7xl items-center justify-between px-6 pt-6">
				{" "}
				{((!isRegistering && !verified && signupStep === "otp") ||
					signupStep === "google-password") && (
					<button
						type="button"
						onClick={() => {
							if (signupStep === "google-password") {
								resetGoogleSignup();
								return;
							}
							setIsRegistering(true);
							setSignupStep("form");
						}}
						className="flex items-center gap-2 text-sm text-gray-200 hover:text-white"
					>
						<FaArrowLeft />
						Back
					</button>
				)}
				<Link to="/" aria-label="Home">
					<img src={onelyk} alt="Home" className="h-8 w-auto sm:h-10" />
				</Link>
			</div>
			<main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-7xl w-full">
					<div className="flex flex-col-reverse lg:grid lg:grid-cols-12 lg:gap-16 items-center">
						
						{/* Left Side Content */}
						<div className="w-full lg:col-span-6 flex flex-col justify-center text-center lg:text-left mt-12 lg:mt-0">
							<div className="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold text-indigo-300 bg-indigo-500/10 ring-1 ring-inset ring-indigo-500/20 mb-6 mx-auto lg:mx-0 w-max">
								✨ Special Offer
							</div>
							<h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl mb-4 lg:mb-6 leading-tight">
								Start with <br className="hidden lg:block"/> <span className="text-indigo-400">200 free credits</span> today.
							</h1>
							<p className="text-base sm:text-lg text-gray-300 max-w-xl mx-auto lg:mx-0">
								Experience the full power of our platform instantly. No credit card required. Cancel anytime.
							</p>
							
							<div className="hidden lg:grid mt-10 grid-cols-2 gap-8 border-t border-gray-700/50 pt-10">
								<div>
									<div className="text-white font-semibold text-lg">Instant Access</div>
									<div className="mt-2 text-sm text-gray-400">Jump right into managing your properties and reviews.</div>
								</div>
								<div>
									<div className="text-white font-semibold text-lg">Zero Risk</div>
									<div className="mt-2 text-sm text-gray-400">No payment info needed until you're ready to upgrade.</div>
								</div>
							</div>
						</div>

						{/* Right Side Form */}
						<div className="w-full lg:col-span-6 flex justify-center lg:justify-end">
							<div className="w-full max-w-md rounded-2xl border border-gray-200/90 bg-white px-6 py-8 sm:px-10 shadow-2xl">
			{signupStep === "google-password" ? (
				<>
					<h2 className="mt-4 sm:mt-5 text-center text-base sm:text-lg font-semibold text-gray-900">
						Create a password
					</h2>
					<p className="mt-2 text-center text-xs sm:text-sm text-gray-700">
						{googleSignupProfile?.email ? (
							<>
								Signing up as{" "}
								<span className="font-medium">{googleSignupProfile.email}</span>
								. Create a password to finish signup and sign in with Google or
								email next time.
							</>
						) : (
							"Google sign-in succeeded. Create a password to finish signup and sign in with Google or email next time."
						)}
					</p>
					<form
						onSubmit={handleGooglePasswordSubmit}
						className="mt-7 grid grid-cols-1 gap-y-5"
					>
						<div className="col-span-full">
							<div className="relative col-span-full">
								<TextField
									label="Password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									required
									onChange={handleGooglePasswordChange}
									value={googlePasswordForm.password}
								/>
								<button
									type="button"
									className="absolute right-0 top-8 sm:top-10 flex items-center pr-3 text-sm"
									onClick={() => setShowPassword(!showPassword)}
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<FaEyeSlash className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
									) : (
										<FaEye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
									)}
								</button>
							</div>
							<div className="text-red-500 text-xs mt-1">
								{inputErrors.password && <span>{inputErrors.password}</span>}
							</div>
						</div>
						<div className="col-span-full">
							<TextField
								label="Confirm password"
								name="confirmPassword"
								type={showPassword ? "text" : "password"}
								autoComplete="new-password"
								required
								onChange={handleGooglePasswordChange}
								value={googlePasswordForm.confirmPassword}
							/>
							<div className="text-red-500 text-xs mt-1">
								{inputErrors.confirmPassword && (
									<span>{inputErrors.confirmPassword}</span>
								)}
							</div>
						</div>
						<div className="col-span-full">
							<label className="flex items-start gap-2 text-xs sm:text-sm">
								<input
									type="checkbox"
									checked={isChecked}
									onChange={(e) => setIsChecked(e.target.checked)}
									required
									className="mt-0.5 sm:mt-1 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0"
								/>
								<span className="leading-tight">
									I agree to the{" "}
									<button
										type="button"
										onClick={() => openModal("terms")}
										className="text-blue-500 underline hover:text-blue-700"
									>
										Terms and Conditions
									</button>{" "}
									and{" "}
									<button
										type="button"
										onClick={() => openModal("privacy")}
										className="text-blue-500 underline hover:text-blue-700"
									>
										Privacy Policy
									</button>
								</span>
							</label>
							<div className="text-red-500 text-xs mt-1">
								{inputErrors.termsAccepted && (
									<span>{inputErrors.termsAccepted}</span>
								)}
							</div>
						</div>
						<div className="col-span-full">
							<Button
								type="submit"
								variant="solid"
								color="blue"
								className="w-full text-sm sm:text-base"
								disabled={isLoading}
							>
								Complete sign up
							</Button>
						</div>
					</form>
				</>
			) : isRegistering ? (
				<>
					<h2 className="mt-4 sm:mt-5 text-center text-base sm:text-lg font-semibold text-gray-900">
						Get started for free
					</h2>
					<p className="mt-2 text-center text-xs sm:text-sm text-gray-700">
						Already registered?{" "}
						<a href="/" className="font-medium text-blue-600 hover:underline">
							Sign in
						</a>{" "}
						to your account.
					</p>
					<form
						onSubmit={handleRegisterSubmit}
						className="mt-7 grid grid-cols-1 gap-y-5"
					>
						<div className="relative col-span-full">
							<TextField
								label="Name"
								name="name"
								type="text"
								autoComplete="name"
								required
								onChange={handleChange}
								value={formData.name}
							/>
							<div className="text-red-500 text-xs">
								{inputErrors.name && <span>{inputErrors.name}</span>}
							</div>
						</div>
						<div className="relative col-span-full">
							<div className="absolute right-0 top-0">
								<div className="relative flex items-center gap-1 group">
									<InformationCircleIcon className="h-4 w-4 text-gray-400 group-hover:text-indigo-600 cursor-pointer" />

									{/* Tooltip */}
									<div
										className="absolute right-0 top-6 hidden group-hover:block
            bg-black text-white text-xs px-2 py-1 rounded shadow-md w-max"
									>
										This signup is for individual users, not businesses
									</div>
								</div>
							</div>
							<TextField
								className="col-span-full"
								label="Email address"
								name="email"
								type="email"
								autoComplete="email"
								required
								onChange={handleChange}
								value={formData.email}
							/>

							<div className="text-red-500 text-xs">
								{inputErrors.email && <span>{inputErrors.email}</span>}
							</div>
						</div>


						<div className="col-span-full">
							<div className="relative col-span-full">
								<TextField
									label="Password"
									name="password"
									type={showPassword ? "text" : "password"}
									autoComplete="new-password"
									required
									onChange={handleChange}
									value={formData.password}
								/>
								<button
									type="button"
									className="absolute right-0 top-8 sm:top-10 flex items-center pr-3 text-sm"
									onClick={() => setShowPassword(!showPassword)}
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<FaEyeSlash className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
									) : (
										<FaEye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
									)}
								</button>
							</div>
							<div className="text-red-500 text-xs mt-1">
								{inputErrors.password && <span>{inputErrors.password}</span>}
							</div>
						</div>		
						<div className="col-span-full">
							<label className="flex items-start gap-2 text-xs sm:text-sm">
								<input
									type="checkbox"
									checked={isChecked}
									onChange={(e) => setIsChecked(e.target.checked)}
									required
									className="mt-0.5 sm:mt-1 h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0"
								/>
								<span className="leading-tight">
									I agree to the{" "}
									<button
										type="button"
										onClick={() => openModal("terms")}
										className="text-blue-500 underline hover:text-blue-700"
									>
										Terms and Conditions
									</button>{" "}
									and{" "}
									<button
										type="button"
										onClick={() => openModal("privacy")}
										className="text-blue-500 underline hover:text-blue-700"
									>
										Privacy Policy
									</button>
								</span>
							</label>
							<div className="text-red-500 text-xs mt-1">
								{inputErrors.termsAccepted && (
									<span>{inputErrors.termsAccepted}</span>
								)}
							</div>
						</div>
						<div className="col-span-full flex flex-col items-center justify-center">
							<ReCaptchaField
								onVerify={(token) => {
									setCaptchaToken(token);

									if (token) {
										setInputErrors((prev) => ({
											...prev,
											captcha: null,
										}));
									}
								}}
							/>
							<div className="text-red-500 text-xs text-center mt-1">
								{inputErrors.captcha && <span>{inputErrors.captcha}</span>}
							</div>
						</div>

						<div className="col-span-full">
							<Button
								type="submit"
								variant="solid"
								color="blue"
								className="w-full text-sm sm:text-base"
								disabled={googleLoading}
							>
								<span>
									Sign up <span aria-hidden="true">&rarr;</span>
								</span>
							</Button>
						</div>
						<div className="col-span-full">
							<AuthOrDivider />
							<GoogleAuthButton
								type="button"
								loading={googleLoading}
								disabled={isLoading}
								onClick={handleGoogleClick}
							/>
						</div>
					</form>
				</>
			) : signupStep === "otp" ? (
				<>
					{!verified ? (
						<form
							onSubmit={handleOtpSubmit}
							className="mt-7 grid grid-cols-1 gap-y-5"
						>
							<div className="col-span-full">
								<h2 className="text-base sm:text-lg font-semibold text-gray-900">
									Verify your email
								</h2>
								<p className="mt-2 text-xs sm:text-sm text-gray-700">
									Enter the OTP sent to your email.
								</p>
							</div>
							<TextField
								className="col-span-full"
								label="OTP"
								name="otp"
								type="text"
								required
								onChange={handleOtpChange}
								value={otp}
							/>
							<div className="col-span-full flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
								<Button
									type="submit"
									variant="solid"
									color="blue"
									className="w-full sm:w-auto text-sm sm:text-base"
								>
									<span>
										Verify <span aria-hidden="true">&rarr;</span>
									</span>
								</Button>
								<Button
									type="button"
									variant="outline"
									color="gray"
									className="w-full sm:w-auto sm:ml-4 text-sm sm:text-base"
									onClick={handleResendOtp}
									disabled={resendCountdown > 0}
								>
									{resendCountdown > 0
										? `Resend OTP (${resendCountdown})`
										: "Resend OTP"}
								</Button>
							</div>
						</form>
					) : (
						<div className="mt-8 sm:mt-10 text-center">
							<h3 className="text-base sm:text-lg font-semibold text-green-600">
								Email verified successfully!
							</h3>
							<p className="mt-2 text-xs sm:text-sm text-gray-700">
								Your email has been verified. Redirecting to login page...
							</p>
						</div>
					)}
				</>
			) : null}
			<ConditionsModal
				isOpen={isModalOpen}
				onClose={closeModal}
				type={modalContent}
			/>
							</div>
						</div>
					</div>
				</div>
			</main>
			</div>
		</div>
	);
}
